import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";

import { cn } from "@/lib/utils";

// The `transition-[...]` list is explicit (not `transition-all`) so unrelated
// property changes don't animate. If you add a new animated property to a
// variant (opacity, outline-color, filter, …), extend that variant's transition
// list too or it will snap instead of ease. `motion-reduce:` disables the
// press-scale + transition for users who requested reduced motion (web mirror of
// the mobile Button's `useReducedMotion()` guard).
const buttonVariants = cva(
  "inline-flex items-center justify-center rounded-lg text-base transition-[color,background-color,border-color,transform] duration-150 ease-signature focus-visible:outline-none active:scale-[0.97] disabled:pointer-events-none disabled:opacity-50 motion-reduce:transition-none motion-reduce:active:scale-100",
  {
    variants: {
      variant: {
        default:
          "inline-flex items-center justify-center gap-3 bg-primary font-semibold leading-none text-primary-foreground",
        "default-dark":
          "inline-flex items-center justify-center gap-3 bg-secondary font-semibold leading-none text-primary",
        outline:
          "inline-flex items-center justify-center gap-3 border border-input font-semibold leading-none text-primary",
        rounded:
          "inline-flex items-center justify-center gap-3 rounded-full border border-input leading-none text-primary",
        text: "inline-flex items-center justify-center gap-3 !px-0 !py-0 text-primary",
        pill: "pill-button inline-flex items-center justify-center gap-3 rounded-full font-semibold leading-none",
        "pill-selected":
          "pill-button-selected inline-flex items-center justify-center gap-3 rounded-full font-semibold leading-none",
        "pill-2":
          "pill-2-button inline-flex items-center justify-center gap-3 rounded-full leading-none",
        "pill-2-selected":
          "pill-2-button-selected inline-flex items-center justify-center gap-3 rounded-full leading-none",
        "3d-gold":
          "rounded-full bg-brand-gold p-2 !px-8 font-semibold shadow-[0_9px_0_#AA7650] transition-[transform,box-shadow] ease-out hover:translate-y-1 hover:shadow-[0_4px_0px_#AA7650]",
        "3d-white":
          "rounded-full bg-foreground p-2 !px-8 font-semibold shadow-[0_9px_0_#B0B0B0] transition-[transform,box-shadow] ease-out hover:translate-y-1 hover:shadow-[0_4px_0px_#B0B0B0]",
        "gradient-dark":
          "rounded-full border-[0.692px] border-pure-white/70 bg-gradient-to-br from-pure-white/20 to-transparent text-sm font-medium text-foreground backdrop-blur-[14.5px]",
        "orange-gradient":
          "group relative h-11 overflow-hidden rounded-lg bg-gradient-to-l from-brand-gold to-brand-gold-dark px-6 py-2.5 text-base font-normal text-foreground shadow-[0px_4px_25px_0px_rgba(9,25,52,1.00)] outline outline-1 outline-brand-gold-outline transition-[transform,box-shadow] duration-300 hover:shadow-lg hover:shadow-brand-gold/20",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "hover:bg-transparent",
        dark: "rounded-full bg-secondary text-sm font-medium text-secondary-foreground",
        "search-gold":
          "inline-flex items-center justify-center gap-2 rounded-lg border border-rose-gold-500 bg-gradient-to-l from-rose-gold-400 to-rose-gold-700 text-pure-white",
      },
      size: {
        default: "px-6 py-3",
        sm: "px-3 py-2 text-sm",
        md: "px-4 py-4 text-sm",
        lg: "px-8 py-6 text-lg",
        none: "",
        icon: "h-10 w-10 rounded-full leading-none",
        "icon-sm": "h-8 w-8 rounded-full leading-none",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean;
  loadingClassName?: string;
  v2Effect?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      loading,
      children,
      disabled,
      v2Effect,
      loadingClassName,
      style,
      ...props
    },
    ref
  ) => {
    // No need for inline gradient style for orange-gradient variant
    // as it's handled by Tailwind's bg-gradient-to-l class
    const gradientStyle = style;

    return (
      <button
        className={cn(
          buttonVariants({ variant, size, className }),
          v2Effect &&
            "h-16 rounded-full bg-brand-gold p-2 !px-8 shadow-[0_9px_0_#AA7650] transition-[transform,box-shadow] ease-out hover:translate-y-1 hover:shadow-[0_4px_0px_#AA7650]"
        )}
        style={gradientStyle}
        ref={ref}
        disabled={loading || disabled}
        {...props}
      >
        {variant === "orange-gradient" ? (
          <>
            <span className="relative z-10 flex items-center justify-center">
              {children}
              {loading && (
                <Loader2
                  className={cn("ml-2 h-4 w-4 animate-spin", loadingClassName)}
                />
              )}
            </span>
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-foreground/20 to-transparent opacity-0 transition-opacity duration-500 group-hover:translate-x-full group-hover:opacity-100" />
          </>
        ) : (
          <>
            {children}
            {loading && (
              <Loader2
                className={cn("mr-2 h-4 w-4 animate-spin", loadingClassName)}
              />
            )}
          </>
        )}
      </button>
    );
  }
);
Button.displayName = "Button";

export { buttonVariants };
export default Button;
